import {stripeLinkForHost} from '@stripe-internal/stripe-link';
import {
  ExternalUrl,
  Route,
  RouteNames,
  RouteParameter,
  RouteResult,
  // This is exactly the place that we *should* be using this import
  // eslint-disable-next-line no-restricted-imports
  createRoute as createSailRoute,
} from '@sail/navigation';
import {
  DEFAULT_PREFIX,
  PathPrefixes,
  PrefixablePair,
  getPrefixForPair,
  isGlobalizationPrefix,
} from 'mkt-ssr/lib/globalization';
import {STRIPE_ENV} from 'mkt-ssr/lib/env';
import {TransformedUrl} from 'mkt-ssr/lib/routing/LinkResolver/types';
import {
  ContentfulConfig,
  DEFAULT_CONTENTFUL_ENV,
} from 'mkt-ssr/lib/contentful/config';
import {
  ProtocolLink,
  ValidMappedProtocols,
} from '@stripe-internal/mkt-ssr-models';

interface LinkResolverConfig {
  resolvedHost: string;
  actualHost: string;
  currentGlobalization: PrefixablePair;
  stripeEnv: STRIPE_ENV;
  contentfulConfig: ContentfulConfig;
}

const PROTOCOL_TO_HOST_MAPPING: {[K in ValidMappedProtocols]: string | null} = {
  // For mkt: and site: links we always
  // resolve these as root-relative links.
  // This is because these components should *never* be
  // rendered outside of that host. While there are
  // a very narrow number of routes that will actually
  // resolve from site-srv, meaning that for devboxes and
  // internal domains they may 404 on the current domain
  // these routes are so few and far between, and are *all*
  // in the process of either being moved to mkt-srv or their
  // own sub-domain entirely this error scenario, which
  // can only happen internally. As such, the complexity required to
  // support it is far more likely to create actual user-facing
  // issues in order to address an extremely narrow potential failure case.
  mkt: null,
  site: null,
  link: 'link.com',
  press: 'press.stripe.com',
  docs: 'docs.stripe.com',
  manage: 'dashboard.stripe.com',
};

const CUSTOM_PROTOCOL_PATTERN = new RegExp(
  `^(${Object.keys(PROTOCOL_TO_HOST_MAPPING).join('|')}):`,
);

const UNPREFIXED_HOSTS = new Set(['stripe.com', 'mkt.corp.stripe.com']);

const GLOBALIZED_HOSTS = new Set([
  'link.com',
  'stripe.com',
  'mkt.corp.stripe.com',
]);

const ALIASED_HOST_MAPPINGS = new Map([
  ['link.com', 'link'],
  ['press.stripe.com', 'press'],
]);

const ANY_PREFIX_PATTERN = new RegExp(
  `^/(?:${Object.keys(PathPrefixes).join('|')})(?=/|$)`,
  'i',
);

function isHostProtocol(
  protocol: string,
): protocol is keyof typeof PROTOCOL_TO_HOST_MAPPING {
  return protocol in PROTOCOL_TO_HOST_MAPPING;
}

function isStripeDomain(host: string): boolean {
  return (
    !!host.match(/[\w_]+\.(corp\.)?(stripe\.com|link\.com)$/) ||
    !!host.match(/^(stripe\.com|link\.com)$/)
  );
}

function globalizePath(path: string, globalization: PrefixablePair) {
  let globalizationStrippedPath = path.replace(ANY_PREFIX_PATTERN, '');
  const prefix = getPrefixForPair(globalization);
  if (prefix && prefix !== DEFAULT_PREFIX.prefix) {
    if (globalizationStrippedPath === '/') {
      globalizationStrippedPath = '';
    }

    return `/${prefix}${globalizationStrippedPath}`;
  } else {
    return globalizationStrippedPath;
  }
}

function transformProtocolLink(input: string): string {
  const [, protocol] = input.match(CUSTOM_PROTOCOL_PATTERN) || [];
  if (protocol && isHostProtocol(protocol)) {
    const host = PROTOCOL_TO_HOST_MAPPING[protocol];
    const mappedHost: string | null = host;
    return input.replace(
      CUSTOM_PROTOCOL_PATTERN,
      mappedHost ? `https://${mappedHost}` : '',
    );
  }

  return input;
}

function extractInlineGlobalization(
  url: URL,
  currentGlobalization: PrefixablePair,
): [PrefixablePair, URL] {
  let prefixPair: PrefixablePair = currentGlobalization;
  let updatedUrl = url;
  if (GLOBALIZED_HOSTS.has(url.host)) {
    const [maybeGlobalization] = url.pathname.replace(/^\//, '').split('/');
    if (isGlobalizationPrefix(maybeGlobalization)) {
      prefixPair = PathPrefixes[maybeGlobalization];
      updatedUrl = new URL(url);
      updatedUrl.pathname = url.pathname.replace(
        new RegExp(`^/?${getPrefixForPair(prefixPair)}`),
        '',
      );
    }
  }

  return [prefixPair, updatedUrl];
}

function addGlobalizationToUrl(url: URL, globalization: PrefixablePair): URL {
  if (GLOBALIZED_HOSTS.has(url.host)) {
    url.pathname = globalizePath(url.pathname, globalization);
  }

  return url;
}

function isAliasedHost(host: string, env: STRIPE_ENV): boolean {
  return env === STRIPE_ENV.DEV && ALIASED_HOST_MAPPINGS.has(host);
}

function isRootRelativeHost(host: string, env: STRIPE_ENV): boolean {
  return UNPREFIXED_HOSTS.has(host) || isAliasedHost(host, env);
}

function hostAliasParams(host: string): {$host?: string} {
  const alias = ALIASED_HOST_MAPPINGS.get(host);
  // NB: It's safe to skip the ENV check here because
  // this function is only applied in places where we've already
  // determined this to be an approriate action.
  if (alias) {
    return {$host: alias};
  } else {
    return {};
  }
}

function applyStripeLinkToUrl(url: URL, currentHost: string): URL {
  const stringifiedURL = url.toString();
  if (isStripeDomain(url.host)) {
    // stripeLinkForHost strips query params, but we need to carry them over
    const stripeURL = new URL(stripeLinkForHost(stringifiedURL, currentHost));
    stripeURL.search = url.search;

    return stripeURL;
  }
  return url;
}

export type LinkResolverResult = TransformedUrl | Required<Route> | Route;

export class LinkResolver {
  constructor(private config: LinkResolverConfig) {
    this.createRoute = this.createRoute.bind(this);
  }

  private get globalSearchParams() {
    const params: {
      __previewId?: string;
      __react?: string;
      __environment?: string;
    } = {};
    if (this.config.contentfulConfig.isPreview) {
      params.__previewId = '';
    }
    if (this.config.contentfulConfig.environment !== DEFAULT_CONTENTFUL_ENV) {
      params.__environment = this.config.contentfulConfig.environment;
    }
    return params;
  }

  public createRoute<T extends RouteNames>(
    route: RouteResult<T>,
  ): Required<Route>;

  public createRoute<T extends RouteNames>(route: RouteParameter<T>): Route;

  public createRoute(urlLike: ExternalUrl): TransformedUrl | Route;

  public createRoute(urlLike: ProtocolLink): TransformedUrl | Route;

  public createRoute<T extends RouteNames>(
    urlLike: ExternalUrl | ProtocolLink | RouteParameter<T> | RouteResult<T>,
  ): TransformedUrl | Required<Route> | Route;

  public createRoute<T extends RouteNames>(
    urlLike: ExternalUrl | ProtocolLink | RouteParameter<T> | RouteResult<T>,
  ): TransformedUrl | Required<Route> | Route {
    if (typeof urlLike === 'string') {
      const transformedUrl = transformProtocolLink(urlLike);
      let globalization = this.config.currentGlobalization;

      let url = new URL(transformedUrl, `https://${this.config.resolvedHost}`);
      if (url.protocol === 'https:') {
        [globalization, url] = extractInlineGlobalization(url, globalization);
        // For root relative hosts (stripe.com, mkt.corp.stripe.com,
        //  aliased hosts) we generate a Sail route, and apply behavior
        // from there. For all others we handle them as URLs.
        if (isRootRelativeHost(url.host, this.config.stripeEnv)) {
          return createSailRoute({
            name: 'CATCH_ALL_GLOB',
            params: {
              glob:
                url.pathname.length === 0 || url.pathname === '/'
                  ? []
                  : url.pathname.replace(/^\//, '').split('/'),
              // Only override this if it should actually be different,
              // otherwise depend on carry-over params to handle this
              localized:
                this.config.currentGlobalization !== globalization
                  ? {locale: getPrefixForPair(globalization)}
                  : undefined,
            },
            hash: url.hash,
            searchSafe: {
              ...this.globalSearchParams,
              ...hostAliasParams(url.host),
              ...Object.fromEntries(url.searchParams.entries()),
            },
          });
        } else {
          url = addGlobalizationToUrl(url, globalization);
          url = applyStripeLinkToUrl(url, this.config.actualHost);
        }
      }
      return url.toString() as TransformedUrl;
    } else {
      return createSailRoute({
        ...urlLike,
        searchSafe: {
          ...this.globalSearchParams,
          ...urlLike.searchSafe,
        },
      });
    }
  }
}
